import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Skeleton,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Link,
  Chip,
  TextField,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Cloud as CloudIcon,
  Memory as CPUIcon,
  Storage as StorageIcon,
  DataUsage as MemoryIcon,
  AttachMoney as CostIcon,
  AccountTree as CompartmentIcon,
  Computer as InstanceIcon,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

// Chart components
import { Line, Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';

// Custom components
import PageHeader from '../components/common/PageHeader';
import MetricCard from '../components/common/MetricCard';
import ChartCard from '../components/common/ChartCard';
import LoadingOverlay from '../components/common/LoadingOverlay';
import EmptyState from '../components/common/EmptyState';
import api from '../services/api';
import ociMonitoringApi from '../services/ociMonitoringApi';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Utility function to format numbers
const formatNumber = (num, decimals = 0, isCurrency = false) => {
  if (num === null || num === undefined) return isCurrency ? '₹0' : '0';
  const formattedNum = num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  return isCurrency ? `₹${formattedNum}` : formattedNum;
};

// Utility to get chart colors
const getChartColors = (index = 0, alpha = 1) => {
  const colors = [
    `rgba(58, 134, 255, ${alpha})`,   // blue (primary)
    `rgba(76, 201, 189, ${alpha})`,   // teal (secondary)
    `rgba(255, 183, 77, ${alpha})`,   // amber (tertiary)
    `rgba(255, 153, 102, ${alpha})`,  // orange (quaternary)
    `rgba(76, 175, 80, ${alpha})`,    // green
    `rgba(33, 150, 243, ${alpha})`,   // light blue
    `rgba(156, 39, 176, ${alpha})`,   // purple
    `rgba(233, 30, 99, ${alpha})`,    // pink
    `rgba(0, 150, 136, ${alpha})`,    // teal
  ];

  return colors[index % colors.length];
};

// Cache management functions
const OCI_CACHE_PREFIX = 'oci_dashboard_';
const OCI_CACHE_EXPIRY = 15 * 60 * 1000; // 15 minutes cache expiry

const getCacheKey = (key, region, compartment, startDate, endDate) => {
  return `${OCI_CACHE_PREFIX}${key}_${region || 'all'}_${compartment || 'all'}_${startDate ? formatDateForAPI(new Date(startDate)) : ''}_${endDate ? formatDateForAPI(new Date(endDate)) : ''}`;
};

const getFromCache = (key, region, compartment, startDate, endDate) => {
  const cacheKey = getCacheKey(key, region, compartment, startDate, endDate);
  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    // Check if cache is expired
    if (Date.now() - timestamp > OCI_CACHE_EXPIRY) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch (error) {
    console.error(`Error retrieving ${key} from cache:`, error);
    return null;
  }
};

const saveToCache = (key, data, region, compartment, startDate, endDate) => {
  const cacheKey = getCacheKey(key, region, compartment, startDate, endDate);
  try {
    const cacheObj = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheObj));
  } catch (error) {
    console.error(`Error saving ${key} to cache:`, error);
  }
};

// Utility function to format date for API requests
const formatDateForAPI = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const OCIDashboard = () => {
  const theme = useTheme();

  // State for filters
  const [region, setRegion] = useState('');
  const [compartment, setCompartment] = useState('');
  const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)); // 7 days ago
  const [endDate, setEndDate] = useState(new Date(Date.now() - 24 * 60 * 60 * 1000)); // 1 day ago

  // Add state for memory, CPU, and cost details modals
  const [memoryModalOpen, setMemoryModalOpen] = useState(false);
  const [memoryDetails, setMemoryDetails] = useState([]);
  const [cpuModalOpen, setCpuModalOpen] = useState(false);
  const [cpuDetails, setCpuDetails] = useState([]);
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [costDetails, setCostDetails] = useState({});

  // Data state
  const [regions, setRegions] = useState([]);
  const [compartments, setCompartments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [costData, setCostData] = useState([]);
  const [instancesData, setInstancesData] = useState([]);
  const [cpuTrend, setCpuTrend] = useState([]);
  const [memoryTrend, setMemoryTrend] = useState([]);
  const [storageData, setStorageData] = useState({
    bootVolume: 0,
    blockVolume: 0
  });

  // Loading state
  const [loading, setLoading] = useState({
    regions: false,
    compartments: false,
    summary: false,
    costs: false,
    instances: false,
    metrics: false
  });

  // Fetch regions data
  const fetchRegions = async () => {
    setLoading(prev => ({ ...prev, regions: true }));
    try {
      // Try to get from cache first
      const cachedRegions = getFromCache('regions');
      if (cachedRegions) {
        console.log('Using cached regions data');
        setRegions(cachedRegions);
        setLoading(prev => ({ ...prev, regions: false }));
        return;
      }

      const response = await api.get('/api/oci/regions');
      const regionsData = response.data || [];
      setRegions(regionsData);

      // Save to cache
      saveToCache('regions', regionsData);
    } catch (error) {
      console.error('Error fetching regions:', error);
    } finally {
      setLoading(prev => ({ ...prev, regions: false }));
    }
  };

  // Fetch compartments data
  const fetchCompartments = async (selectedRegion) => {
    setLoading(prev => ({ ...prev, compartments: true }));
    try {
      // Try to get from cache first
      const cachedCompartments = getFromCache('compartments', selectedRegion);
      if (cachedCompartments) {
        console.log('Using cached compartments data');
        setCompartments(cachedCompartments);
        setLoading(prev => ({ ...prev, compartments: false }));
        return;
      }

      const url = selectedRegion
        ? `/api/oci/compartments?region=${selectedRegion}`
        : '/api/oci/compartments';

      const response = await api.get(url);
      const compartmentsData = response.data || [];
      setCompartments(compartmentsData);

      // Save to cache
      saveToCache('compartments', compartmentsData, selectedRegion);
    } catch (error) {
      console.error('Error fetching compartments:', error);
    } finally {
      setLoading(prev => ({ ...prev, compartments: false }));
    }
  };

  // Fetch summary data
  const fetchSummary = async () => {
    setLoading(prev => ({ ...prev, summary: true }));
    try {
      // Try to get from cache first
      const cachedSummary = getFromCache('summary', region, compartment, null, endDate);
      if (cachedSummary) {
        console.log('Using cached summary data');
        setSummary(cachedSummary);
        setLoading(prev => ({ ...prev, summary: false }));
        return;
      }

      const params = new URLSearchParams();
      if (region) params.append('region', region);
      if (compartment) params.append('compartment', compartment);
      params.append('date', formatDateForAPI(endDate));

      const response = await api.get(`/api/oci/summary?${params.toString()}`);
      const summaryData = response.data || null;

      // Fix missing total memory and CPU count if necessary
      if (summaryData) {
        // Ensure total_memory and total_cpu have valid values
        if (summaryData.total_memory === undefined || summaryData.total_memory === null) {
          console.log('Fixing missing total_memory in summary data');
          summaryData.total_memory = summaryData.instances ?
            summaryData.instances.reduce((sum, instance) => sum + (instance.memory || 0), 0) : 0;
        }

        if (summaryData.total_cpu === undefined || summaryData.total_cpu === null) {
          console.log('Fixing missing total_cpu in summary data');
          summaryData.total_cpu = summaryData.instances ?
            summaryData.instances.reduce((sum, instance) => sum + (instance.cpu || 0), 0) : 0;
        }
      }

      setSummary(summaryData);

      // Save to cache
      saveToCache('summary', summaryData, region, compartment, null, endDate);
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoading(prev => ({ ...prev, summary: false }));
    }
  };

  // Fetch costs data
  const fetchCosts = async () => {
    setLoading(prev => ({ ...prev, costs: true }));
    try {
      // Try to get from cache first
      const cachedCosts = getFromCache('costs', region, compartment, startDate, endDate);
      if (cachedCosts) {
        console.log('Using cached cost data');
        setCostData(cachedCosts);
        setLoading(prev => ({ ...prev, costs: false }));
        return;
      }

      // Use the ociMonitoringApi service instead of direct API calls
      const response = await ociMonitoringApi.getCosts({
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate),
        region: region,
        compartment: compartment
      });

      console.log('Costs response in component:', response);

      let costsData = [];

      // Handle both data formats
      if (response && response.data && Array.isArray(response.data)) {
        console.log('Setting cost data array with length:', response.data.length);
        costsData = response.data;
      } else if (response && response.data) {
        // Convert object format to array if needed
        costsData = Object.entries(response.data).map(([date, data]) => ({
          date,
          ...data
        }));
        console.log('Converted cost data to array with length:', costsData.length);
      } else {
        console.warn('No valid cost data found in response');
        costsData = [];
      }

      setCostData(costsData);

      // Save to cache
      saveToCache('costs', costsData, region, compartment, startDate, endDate);
    } catch (error) {
      console.error('Error fetching costs:', error);
      setCostData([]);
    } finally {
      setLoading(prev => ({ ...prev, costs: false }));
    }
  };

  // Fetch instances data
  const fetchInstances = async () => {
    setLoading(prev => ({ ...prev, instances: true }));
    try {
      // Try to get from cache first
      const cachedInstances = getFromCache('instances', region, compartment, startDate, endDate);
      if (cachedInstances) {
        console.log('Using cached instances data');
        setInstancesData(cachedInstances);
        setLoading(prev => ({ ...prev, instances: false }));
        return;
      }

      const params = new URLSearchParams();
      if (region) params.append('region', region);
      if (compartment) params.append('compartment', compartment);
      params.append('start_date', formatDateForAPI(startDate));
      params.append('end_date', formatDateForAPI(endDate));

      const response = await api.get(`/api/oci/instances?${params.toString()}`);
      const instancesData = response.data || [];
      setInstancesData(instancesData);

      // Save to cache
      saveToCache('instances', instancesData, region, compartment, startDate, endDate);
    } catch (error) {
      console.error('Error fetching instances:', error);
    } finally {
      setLoading(prev => ({ ...prev, instances: false }));
    }
  };

  // Fetch metrics trend data
  const fetchMetricsTrend = async () => {
    setLoading(prev => ({ ...prev, metrics: true }));
    try {
      // Try to get from cache first
      const cachedMetrics = getFromCache('metrics', region, compartment, startDate, endDate);
      if (cachedMetrics) {
        console.log('Using cached metrics data');
        setCpuTrend(cachedMetrics.cpuTrend || []);
        setMemoryTrend(cachedMetrics.memoryTrend || []);
        setStorageData(cachedMetrics.storageData || { bootVolume: 0, blockVolume: 0 });
        setLoading(prev => ({ ...prev, metrics: false }));
        return;
      }

      const baseParams = new URLSearchParams();
      if (region) baseParams.append('region', region);
      if (compartment) baseParams.append('compartment', compartment);
      baseParams.append('start_date', formatDateForAPI(startDate));
      baseParams.append('end_date', formatDateForAPI(endDate));

      // Fetch CPU trend
      const cpuParams = new URLSearchParams(baseParams);
      cpuParams.append('metric_type', 'cpu');
      const cpuResponse = await api.get(`/api/oci/metrics/trend?${cpuParams.toString()}`);
      const cpuTrendData = cpuResponse.data || [];
      setCpuTrend(cpuTrendData);

      // Fetch Memory trend
      const memoryParams = new URLSearchParams(baseParams);
      memoryParams.append('metric_type', 'memory');
      const memoryResponse = await api.get(`/api/oci/metrics/trend?${memoryParams.toString()}`);
      const memoryTrendData = memoryResponse.data || [];
      setMemoryTrend(memoryTrendData);

      // Fetch storage data
      const bootVolumeParams = new URLSearchParams(baseParams);
      bootVolumeParams.append('metric_type', 'boot_volume');
      const bootVolumeResponse = await api.get(`/api/oci/metrics/trend?${bootVolumeParams.toString()}`);

      const blockVolumeParams = new URLSearchParams(baseParams);
      blockVolumeParams.append('metric_type', 'block_volume');
      const blockVolumeResponse = await api.get(`/api/oci/metrics/trend?${blockVolumeParams.toString()}`);

      // Get latest values from the trends
      const latestBootVolume = bootVolumeResponse.data?.length
        ? bootVolumeResponse.data[bootVolumeResponse.data.length - 1].value
        : 0;

      const latestBlockVolume = blockVolumeResponse.data?.length
        ? blockVolumeResponse.data[blockVolumeResponse.data.length - 1].value
        : 0;

      const storageDataObj = {
        bootVolume: latestBootVolume,
        blockVolume: latestBlockVolume
      };

      setStorageData(storageDataObj);

      // Save to cache
      saveToCache('metrics', {
        cpuTrend: cpuTrendData,
        memoryTrend: memoryTrendData,
        storageData: storageDataObj
      }, region, compartment, startDate, endDate);

    } catch (error) {
      console.error('Error fetching metrics trend:', error);
    } finally {
      setLoading(prev => ({ ...prev, metrics: false }));
    }
  };

  // Load all data
  const loadAllData = async () => {
    setLoading({
      regions: true,
      compartments: true,
      summary: true,
      costs: true,
      instances: true,
      metrics: true
    });

    try {
      await fetchRegions();
      await fetchCompartments(region);

      // Fetch all other data in parallel
      await Promise.all([
        fetchSummary(),
        fetchCosts(),
        fetchInstances(),
        fetchMetricsTrend()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading({
        regions: false,
        compartments: false,
        summary: false,
        costs: false,
        instances: false,
        metrics: false
      });
    }
  };

  // Handle refresh button click
  const handleRefresh = () => {
    loadAllData();
  };

  // Initialize data load
  useEffect(() => {
    fetchRegions();
  }, []);

  // Update compartments when region changes
  useEffect(() => {
    fetchCompartments(region);
    // Reset compartment selection when region changes
    setCompartment('');
  }, [region]);

  // Update all data when filters change
  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        fetchSummary(),
        fetchCosts(),
        fetchInstances(),
        fetchMetricsTrend()
      ]);
    };

    fetchData();
  }, [region, compartment, startDate, endDate]);

  // Process data for charts

  // Cost distribution chart data
  const costDistributionData = useMemo(() => {
    if (!costData || costData.length === 0) return null;

    // Sum up costs across all days in the range
    const totalCosts = {
      compute: costData.reduce((sum, day) => sum + (day.total_compute_cost || 0), 0),
      storage: costData.reduce((sum, day) => sum +
        (day.total_block_storage_cost || 0) +
        (day.total_file_storage_cost || 0) +
        (day.total_object_storage_cost || 0), 0),
      network: costData.reduce((sum, day) => sum +
        (day.total_load_balancer_cost || 0) +
        (day.total_virtual_cloud_network_cost || 0), 0),
      database: costData.reduce((sum, day) => sum + (day.total_mysql_cost || 0), 0),
      other: costData.reduce((sum, day) => sum +
        (day.total_functions_cost || 0) +
        (day.total_telemetry_cost || 0) +
        (day.total_api_gateway_cost || 0) +
        (day.total_container_engine_cost || 0) +
        (day.total_key_management_cost || 0) +
        (day.total_logging_cost || 0), 0)
    };

    return {
      labels: ['Compute', 'Storage', 'Network', 'Database', 'Other'],
      datasets: [{
        data: [
          totalCosts.compute,
          totalCosts.storage,
          totalCosts.network,
          totalCosts.database,
          totalCosts.other
        ],
        backgroundColor: [
          getChartColors(0, 0.7),
          getChartColors(1, 0.7),
          getChartColors(2, 0.7),
          getChartColors(3, 0.7),
          getChartColors(4, 0.7)
        ],
        borderColor: [
          getChartColors(0),
          getChartColors(1),
          getChartColors(2),
          getChartColors(3),
          getChartColors(4)
        ],
        borderWidth: 1
      }]
    };
  }, [costData]);

  // Cost trend chart data
  const costTrendData = useMemo(() => {
    if (!costData || costData.length === 0) return null;

    return {
      labels: costData.map(item => item.date),
      datasets: [{
        label: 'Total Cost (₹)', // Changed to ₹
        data: costData.map(item => item.grand_total_cost || 0),
        borderColor: getChartColors(0),
        backgroundColor: getChartColors(0, 0.1),
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        pointHoverRadius: 5
      }]
    };
  }, [costData]);

  // CPU trend chart data
  const cpuTrendChartData = useMemo(() => {
    if (!cpuTrend || cpuTrend.length === 0) return null;

    return {
      labels: cpuTrend.map(item => item.date),
      datasets: [{
        label: 'CPU Cores',
        data: cpuTrend.map(item => item.value || 0),
        borderColor: getChartColors(5),
        backgroundColor: getChartColors(5, 0.1),
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        pointHoverRadius: 5
      }]
    };
  }, [cpuTrend]);

  // Memory trend chart data
  const memoryTrendChartData = useMemo(() => {
    if (!memoryTrend || memoryTrend.length === 0) return null;

    return {
      labels: memoryTrend.map(item => item.date),
      datasets: [{
        label: 'Memory (GB)',
        data: memoryTrend.map(item => item.value || 0),
        borderColor: getChartColors(6),
        backgroundColor: getChartColors(6, 0.1),
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        pointHoverRadius: 5
      }]
    };
  }, [memoryTrend]);

  // Instance state distribution
  const instanceStateData = useMemo(() => {
    if (!instancesData || instancesData.length === 0) return null;

    const runningCount = instancesData.filter(instance =>
      instance.instance_state === 'RUNNING').length;
    const stoppedCount = instancesData.filter(instance =>
      instance.instance_state === 'STOPPED').length;
    const terminatedCount = instancesData.filter(instance =>
      instance.instance_state === 'TERMINATED').length;
    const otherCount = instancesData.length - runningCount - stoppedCount - terminatedCount;

    return {
      labels: ['Running', 'Stopped', 'Terminated', 'Other'],
      datasets: [{
        data: [runningCount, stoppedCount, terminatedCount, otherCount],
        backgroundColor: [
          getChartColors(4, 0.7), // Green
          getChartColors(2, 0.7), // Amber
          getChartColors(8, 0.7), // Red
          getChartColors(7, 0.7)  // Grey
        ],
        borderColor: [
          getChartColors(4),
          getChartColors(2),
          getChartColors(8),
          getChartColors(7)
        ],
        borderWidth: 1
      }]
    };
  }, [instancesData]);

  // Storage distribution chart data
  const storageDistributionData = useMemo(() => {
    return {
      labels: ['Boot Volumes', 'Block Volumes'],
      datasets: [{
        data: [storageData.bootVolume || 0, storageData.blockVolume || 0],
        backgroundColor: [
          getChartColors(1, 0.7),
          getChartColors(3, 0.7)
        ],
        borderColor: [
          getChartColors(1),
          getChartColors(3)
        ],
        borderWidth: 1
      }]
    };
  }, [storageData]);

  // Chart options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          color: theme.palette.text.secondary,
        },
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: alpha(theme.palette.divider, 0.2),
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        usePointStyle: true,
        callbacks: {
          label: (tooltipItem) => {
            // Format with currency symbol if the label includes 'Cost'
            const value = tooltipItem.raw;
            if (tooltipItem.dataset.label.includes('Cost')) {
              return `${tooltipItem.dataset.label}: ${formatNumber(value, 2, true)}`; // Uses ₹ symbol
            }
            return `${tooltipItem.dataset.label}: ${formatNumber(value, 2)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: alpha(theme.palette.text.secondary, 0.1),
          drawBorder: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          callback: function (value) {
            // Check if any dataset label includes 'Cost' or '₹' to determine if this is a cost chart
            const isCostChart = this.chart.data.datasets.some(ds =>
              ds.label && (ds.label.includes('Cost') || ds.label.includes('₹'))
            );
            return isCostChart ? formatNumber(value, 0, true) : formatNumber(value);
          },
        },
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      line: {
        tension: 0.4
      },
      point: {
        radius: 2,
        hitRadius: 6,
        hoverRadius: 5
      }
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10,
        left: 10,
        right: 20
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          color: theme.palette.text.secondary,
        },
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: alpha(theme.palette.divider, 0.2),
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        callbacks: {
          label: (tooltipItem) => {
            // Only show currency for cost-related charts
            const isCostChart = tooltipItem.label && (
              tooltipItem.label.includes('Cost') ||
              tooltipItem.label.includes('₹')
            );
            return `${tooltipItem.label}: ${formatNumber(tooltipItem.raw, 2, isCostChart)}`;
          },
        },
      },
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10,
        left: 20,
        right: 20
      }
    },
    cutout: '60%'
  };

  // Memory Details Modal 
  const fetchMemoryDetails = async () => {
    try {
      // Use the exact same memory data that's displayed in the card
      const totalMemory = summary?.total_memory ||
        instancesData.reduce((sum, instance) => sum + (instance.memory || 0), 0);

      // Check if we already have instance details in cache or state
      let details = instancesData;

      // If we don't have enough instances data, fetch it
      if (!details || details.length === 0) {
        // Check cache first
        const cachedDetails = getFromCache('instances', region, compartment, startDate, endDate);
        if (cachedDetails && cachedDetails.length > 0) {
          details = cachedDetails;
        } else {
          // Fetch from API if not in cache
          const instanceParams = new URLSearchParams();
          if (region) instanceParams.append('region', region);
          if (compartment) instanceParams.append('compartment', compartment);
          instanceParams.append('start_date', formatDateForAPI(startDate));
          instanceParams.append('end_date', formatDateForAPI(endDate));

          const instancesResponse = await api.get(`/api/oci/instances?${instanceParams.toString()}`);
          details = instancesResponse.data || [];

          // Cache the new data
          saveToCache('instances', details, region, compartment, startDate, endDate);
        }
      }

      // Calculate running and stopped memory totals from instances
      const runningInstances = details.filter(instance =>
        instance.instance_state === 'RUNNING' || instance.instance_state === 'running'
      );
      const stoppedInstances = details.filter(instance =>
        instance.instance_state === 'STOPPED' || instance.instance_state === 'stopped'
      );

      const totalRunningMemory = runningInstances.reduce((sum, instance) => sum + (instance.memory || 0), 0);
      const totalStoppedMemory = stoppedInstances.reduce((sum, instance) => sum + (instance.memory || 0), 0);

      // If runningMemory + stoppedMemory doesn't match totalMemory, adjust to ensure it matches
      const calculatedTotal = totalRunningMemory + totalStoppedMemory;
      let adjustedRunningMemory = totalRunningMemory;
      let adjustedStoppedMemory = totalStoppedMemory;

      if (calculatedTotal !== totalMemory && calculatedTotal > 0) {
        // Apply a proportional adjustment to make the numbers match the card value
        const adjustmentFactor = totalMemory / calculatedTotal;
        adjustedRunningMemory = totalRunningMemory * adjustmentFactor;
        adjustedStoppedMemory = totalStoppedMemory * adjustmentFactor;
      }

      const avgMemoryPerInstance = details.length ? totalMemory / details.length : 0;

      const avgMemCpuRatio = details.filter(i => i.cpu > 0).length ?
        details.filter(i => i.cpu > 0)
          .reduce((sum, i) => sum + ((i.memory || 0) / (i.cpu || 1)), 0) /
        details.filter(i => i.cpu > 0).length
        : 0;

      // Group by shapes for analysis
      const memoryByShape = {};
      details.forEach(instance => {
        if (instance.vm_shape) {
          if (!memoryByShape[instance.vm_shape]) {
            memoryByShape[instance.vm_shape] = 0;
          }
          memoryByShape[instance.vm_shape] += (instance.memory || 0);
        }
      });

      // If memory by shape doesn't sum to total memory, adjust it proportionally
      const totalShapeMemory = Object.values(memoryByShape).reduce((sum, val) => sum + val, 0);
      if (totalShapeMemory > 0 && totalShapeMemory !== totalMemory) {
        const shapeFactor = totalMemory / totalShapeMemory;
        Object.keys(memoryByShape).forEach(shape => {
          memoryByShape[shape] *= shapeFactor;
        });
      }

      console.log('Memory details calculated:', {
        totalMemory,
        totalRunningMemory: adjustedRunningMemory,
        totalStoppedMemory: adjustedStoppedMemory,
        instanceCount: details.length,
        runningCount: runningInstances.length,
        stoppedCount: stoppedInstances.length
      });

      // Save all analytics to state
      setMemoryDetails({
        instances: details,
        analytics: {
          totalMemory,
          totalRunningMemory: adjustedRunningMemory,
          totalStoppedMemory: adjustedStoppedMemory,
          avgMemoryPerInstance,
          avgMemCpuRatio,
          memoryByShape
        }
      });

      setMemoryModalOpen(true);
    } catch (error) {
      console.error('Error fetching memory details:', error);
    }
  };

  // CPU Details Modal
  const fetchCpuDetails = async () => {
    try {
      // Use the exact same CPU data that's displayed in the card
      const totalCpu = summary?.total_cpu ||
        instancesData.reduce((sum, instance) => sum + (instance.cpu || 0), 0);

      // Check if we already have instance details in cache or state
      let details = instancesData;

      // If we don't have enough instances data, fetch it
      if (!details || details.length === 0) {
        // Check cache first
        const cachedDetails = getFromCache('instances', region, compartment, startDate, endDate);
        if (cachedDetails && cachedDetails.length > 0) {
          details = cachedDetails;
        } else {
          // Fetch from API if not in cache
          const instanceParams = new URLSearchParams();
          if (region) instanceParams.append('region', region);
          if (compartment) instanceParams.append('compartment', compartment);
          instanceParams.append('start_date', formatDateForAPI(startDate));
          instanceParams.append('end_date', formatDateForAPI(endDate));

          const instancesResponse = await api.get(`/api/oci/instances?${instanceParams.toString()}`);
          details = instancesResponse.data || [];

          // Cache the new data
          saveToCache('instances', details, region, compartment, startDate, endDate);
        }
      }

      // Calculate running and stopped CPU totals from instances
      const runningInstances = details.filter(instance =>
        instance.instance_state === 'RUNNING' || instance.instance_state === 'running'
      );
      const stoppedInstances = details.filter(instance =>
        instance.instance_state === 'STOPPED' || instance.instance_state === 'stopped'
      );

      const totalRunningCpu = runningInstances.reduce((sum, instance) => sum + (instance.cpu || 0), 0);
      const totalStoppedCpu = stoppedInstances.reduce((sum, instance) => sum + (instance.cpu || 0), 0);

      // If runningCpu + stoppedCpu doesn't match totalCpu, adjust to ensure it matches
      const calculatedTotal = totalRunningCpu + totalStoppedCpu;
      let adjustedRunningCpu = totalRunningCpu;
      let adjustedStoppedCpu = totalStoppedCpu;

      if (calculatedTotal !== totalCpu && calculatedTotal > 0) {
        // Apply a proportional adjustment to make the numbers match the card value
        const adjustmentFactor = totalCpu / calculatedTotal;
        adjustedRunningCpu = totalRunningCpu * adjustmentFactor;
        adjustedStoppedCpu = totalStoppedCpu * adjustmentFactor;
      }

      const avgCpuPerInstance = details.length ? totalCpu / details.length : 0;

      const avgCpuMemRatio = details.filter(i => i.cpu > 0).length ?
        details.filter(i => i.cpu > 0)
          .reduce((sum, i) => sum + ((i.memory || 0) / (i.cpu || 1)), 0) /
        details.filter(i => i.cpu > 0).length
        : 0;

      // Group by shapes for analysis
      const cpuByShape = {};
      details.forEach(instance => {
        if (instance.vm_shape) {
          if (!cpuByShape[instance.vm_shape]) {
            cpuByShape[instance.vm_shape] = 0;
          }
          cpuByShape[instance.vm_shape] += (instance.cpu || 0);
        }
      });

      // If CPU by shape doesn't sum to total CPU, adjust it proportionally
      const totalShapeCpu = Object.values(cpuByShape).reduce((sum, val) => sum + val, 0);
      if (totalShapeCpu > 0 && totalShapeCpu !== totalCpu) {
        const shapeFactor = totalCpu / totalShapeCpu;
        Object.keys(cpuByShape).forEach(shape => {
          cpuByShape[shape] *= shapeFactor;
        });
      }

      console.log('CPU details calculated:', {
        totalCpu,
        totalRunningCpu: adjustedRunningCpu,
        totalStoppedCpu: adjustedStoppedCpu,
        instanceCount: details.length,
        runningCount: runningInstances.length,
        stoppedCount: stoppedInstances.length
      });

      // Save all analytics to state
      setCpuDetails({
        instances: details,
        analytics: {
          totalCpu,
          totalRunningCpu: adjustedRunningCpu,
          totalStoppedCpu: adjustedStoppedCpu,
          avgCpuPerInstance,
          avgCpuMemRatio,
          cpuByShape
        }
      });

      setCpuModalOpen(true);
    } catch (error) {
      console.error('Error fetching CPU details:', error);
    }
  };

  // Cost Details Modal
  const fetchCostDetails = async () => {
    try {
      let costDataForAnalysis = costData;

      // If we don't have cost data or it's empty, try to get from cache or fetch new
      if (!costDataForAnalysis || costDataForAnalysis.length === 0) {
        // Check cache first
        const cachedCosts = getFromCache('costs', region, compartment, startDate, endDate);
        if (cachedCosts && cachedCosts.length > 0) {
          console.log('Using cached cost data for details');
          costDataForAnalysis = cachedCosts;
        } else {
          console.log("No cost data available, fetching fresh data");
          await fetchCosts();
          costDataForAnalysis = costData; // Use the newly fetched data
        }
      }

      // Calculate totals by category
      const totalCompute = costDataForAnalysis.reduce((sum, day) => sum + (day.total_compute_cost || 0), 0);
      const totalStorage = costDataForAnalysis.reduce((sum, day) => sum +
        (day.total_block_storage_cost || 0) +
        (day.total_file_storage_cost || 0) +
        (day.total_object_storage_cost || 0), 0);
      const totalNetwork = costDataForAnalysis.reduce((sum, day) => sum +
        (day.total_load_balancer_cost || 0) +
        (day.total_virtual_cloud_network_cost || 0), 0);
      const totalDatabase = costDataForAnalysis.reduce((sum, day) => sum + (day.total_mysql_cost || 0), 0);
      const totalOther = costDataForAnalysis.reduce((sum, day) => sum +
        (day.total_functions_cost || 0) +
        (day.total_telemetry_cost || 0) +
        (day.total_api_gateway_cost || 0) +
        (day.total_container_engine_cost || 0) +
        (day.total_key_management_cost || 0) +
        (day.total_logging_cost || 0), 0);

      // Calculate daily averages
      const days = costDataForAnalysis.length || 1;
      const grandTotal = costDataForAnalysis.reduce((sum, day) => sum + (day.grand_total_cost || 0), 0);
      const avgDailyTotal = grandTotal / days;
      const avgDailyCompute = totalCompute / days;
      const avgDailyStorage = totalStorage / days;

      // Save all analytics to state
      setCostDetails({
        rawData: costDataForAnalysis,
        analytics: {
          grandTotal,
          totalCompute,
          totalStorage,
          totalNetwork,
          totalDatabase,
          totalOther,
          avgDailyTotal,
          avgDailyCompute,
          avgDailyStorage,
          days
        }
      });

      console.log("Cost modal details prepared:", {
        grandTotal,
        days,
        avgDailyTotal,
        totalCompute,
        totalStorage
      });

      setCostModalOpen(true);
    } catch (error) {
      console.error('Error preparing cost details:', error);
    }
  };

  // Modal style
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 1200,
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    overflow: 'auto',
    outline: 'none',
    border: 'none',
  };

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="OCI Resource Monitor"
        subtitle="Monitor Oracle Cloud Infrastructure resources and costs"
        icon={CloudIcon}
        actions={
          <Box display="flex" gap={2} flexWrap="wrap">
            {/* Region Selector */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Region</InputLabel>
              <Select
                value={region}
                label="Region"
                onChange={(e) => setRegion(e.target.value)}
                disabled={loading.regions}
              >
                <MenuItem value="">All Regions</MenuItem>
                {regions.map((reg) => (
                  <MenuItem key={reg} value={reg}>{reg}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Compartment Selector */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Compartment</InputLabel>
              <Select
                value={compartment}
                label="Compartment"
                onChange={(e) => setCompartment(e.target.value)}
                disabled={loading.compartments}
              >
                <MenuItem value="">All Compartments</MenuItem>
                {compartments.map((comp) => (
                  <MenuItem key={comp} value={comp}>{comp}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date Range Input - Using simple inputs instead of DatePicker */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={formatDateForAPI(startDate)}
                onChange={(e) => setStartDate(new Date(e.target.value))}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: formatDateForAPI(endDate) }}
              />

              <TextField
                label="End Date"
                type="date"
                size="small"
                value={formatDateForAPI(endDate)}
                onChange={(e) => setEndDate(new Date(e.target.value))}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: formatDateForAPI(startDate),
                  max: formatDateForAPI(new Date())
                }}
              />
            </Box>

            {/* Refresh Button */}
            <Tooltip title="Refresh Data">
              <IconButton
                onClick={handleRefresh}
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Instances"
            value={formatNumber(instancesData.length)}
            subtitle={`${summary?.active_instances || 0} active / ${summary?.inactive_instances || 0} inactive`}
            icon={InstanceIcon}
            color="primary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total CPUs"
            value={formatNumber(summary?.total_cpu ||
              // Calculate from instances if not provided by backend
              instancesData.reduce((sum, instance) => sum + (instance.cpu || 0), 0)
            )}
            subtitle="Cores allocated"
            icon={CPUIcon}
            color="secondary"
            onClick={fetchCpuDetails}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Memory"
            value={formatNumber(summary?.total_memory ||
              // Calculate from instances if not provided by backend
              instancesData.reduce((sum, instance) => sum + (instance.memory || 0), 0),
              1
            )}
            subtitle="GB allocated"
            icon={MemoryIcon}
            color="tertiary"
            onClick={fetchMemoryDetails}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Cost"
            value={formatNumber(
              costData.length
                ? costData.reduce((sum, day) => sum + (day?.grand_total_cost || 0), 0)
                : 0,
              2,
              true
            )} // Sum of all days in range
            subtitle="Current period"
            icon={CostIcon}
            color="quaternary" // This uses the orange/quaternary color
            onClick={fetchCostDetails}
            sx={{
              // Force the h4 text to be orange with important flag and direct color
              '& .MuiTypography-h4': {
                color: '#ff9966 !important'
              }
            }}
          />
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={8}>
          <ChartCard
            title="Cost Trend"
            subtitle="Daily cost over the selected period"
            height={400} // Increased from 320 to 400
            chart={
              costTrendData ? (
                <div className="cost-trend-chart" style={{ height: '100%', width: '100%' }}>
                  <Line data={costTrendData} options={lineChartOptions} />
                </div>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No cost data available</Typography>
                </Box>
              )
            }
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <ChartCard
            title="Cost Distribution"
            subtitle="By service category"
            height={400} // Increased from 320 to 400
            chart={
              costDistributionData ? (
                <div className="cost-distribution-chart" style={{ height: '100%', width: '100%' }}>
                  <Pie data={costDistributionData} options={pieChartOptions} />
                </div>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No cost data available</Typography>
                </Box>
              )
            }
          />
        </Grid>
      </Grid>


      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <ChartCard
            title="CPU Allocation Trend"
            subtitle="Total CPU cores over time"
            height={280}
            chart={
              cpuTrendChartData ? (
                <Line data={cpuTrendChartData} options={lineChartOptions} />
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No CPU data available</Typography>
                </Box>
              )
            } />
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Memory Allocation Trend"
            subtitle="Total memory (GB) over time"
            height={280}
            chart={
              memoryTrendChartData ? (
                <Line data={memoryTrendChartData} options={lineChartOptions} />
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No memory data available</Typography>
                </Box>
              )
            }
          />
        </Grid>
      </Grid>


      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Instance States"
            subtitle="Distribution of instance states"
            height={280}
            chart={
              instanceStateData ? (
                <Pie data={instanceStateData} options={pieChartOptions} />
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No instance data available</Typography>
                </Box>
              )
            }
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Storage Distribution"
            subtitle="Boot volumes vs. block volumes (GB)"
            height={280}
            chart={
              <Pie data={storageDistributionData} options={pieChartOptions} />
            }
          />
        </Grid>
      </Grid>


      <Box mt={4}>
        <Typography variant="h6" fontWeight="medium" mb={2}>
          Recent Instances
        </Typography>

        {instancesData && instancesData.length > 0 ? (
          <Paper
            sx={{
              overflow: 'hidden',
              borderRadius: '16px',
              boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`
            }}
          >
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: 800
              }}>
                <thead>
                  <tr>
                    <th style={{
                      textAlign: 'left',
                      padding: '12px 16px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      Instance Name
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '12px 16px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      Region
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '12px 16px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      Compartment
                    </th>
                    <th style={{
                      textAlign: 'center',
                      padding: '12px 16px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      Status
                    </th>
                    <th style={{
                      textAlign: 'center',
                      padding: '12px 16px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      CPU
                    </th>
                    <th style={{
                      textAlign: 'center',
                      padding: '12px 16px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      Memory (GB)
                    </th>
                    <th style={{
                      textAlign: 'right',
                      padding: '12px 16px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}>
                      Cost (₹)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {instancesData.slice(0, 5).map((instance, index) => (
                    <tr key={instance._id || instance.instance_id || index} style={{
                      backgroundColor: index % 2 === 0
                        ? 'transparent'
                        : alpha(theme.palette.primary.main, 0.02)
                    }}>
                      <td style={{
                        padding: '12px 16px',
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`
                      }}>
                        {instance.instance_name}
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`
                      }}>
                        {instance.region_name}
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`
                      }}>
                        {instance.compartment_name}
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`
                      }}>
                        <Chip
                          label={instance.instance_state}
                          size="small"
                          sx={{
                            backgroundColor: instance.instance_state === 'RUNNING'
                              ? alpha(theme.palette.success.main, 0.1)
                              : instance.instance_state === 'STOPPED'
                                ? alpha(theme.palette.warning.main, 0.1)
                                : alpha(theme.palette.error.main, 0.1),
                            color: instance.instance_state === 'RUNNING'
                              ? theme.palette.success.main
                              : instance.instance_state === 'STOPPED'
                                ? theme.palette.warning.main
                                : theme.palette.error.main,
                            fontWeight: 500,
                            borderRadius: '12px'
                          }}
                        />
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`
                      }}>
                        {instance.cpu || 'N/A'}
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`
                      }}>
                        {instance.memory ? instance.memory.toFixed(1) : 'N/A'}
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        textAlign: 'right',
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`
                      }}>
                        {/* Check various property names for cost - different APIs may use different names */}
                        {formatNumber(
                          instance.compute_cost ||
                          instance.total_cost ||
                          instance.cost ||
                          (instance.total_cost && (
                            instance.boot_volume_price ||
                            (instance.block_volume_prices && instance.block_volume_prices.reduce((sum, price) => sum + price, 0))
                          )) ||
                          0,
                          2,
                          true
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>

            {instancesData.length > 5 && (
              <Box sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'center',
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.05)}`
              }}>
                <Button
                  component={RouterLink}
                  to="/oci/instances"
                  variant="outlined"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    fontWeight: 500
                  }}
                >
                  View All Instances
                </Button>
              </Box>
            )}
          </Paper>
        ) : (
          <EmptyState
            title="No instances found"
            message="No instance data is available for the selected filters."
            icon={InstanceIcon}
            height={200}
          />
        )}
      </Box>

      {/* Quick Links and Actions */}
      <Grid container spacing={3} mt={4}>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: '16px',
              boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`
            }}
          >
            <Typography variant="h6" fontWeight="medium" mb={2}>
              Quick Actions
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  component={RouterLink}
                  to="/oci/instances"
                  fullWidth
                  variant="outlined"
                  startIcon={<InstanceIcon />}
                  sx={{
                    py: 1.5,
                    textTransform: 'none',
                    borderRadius: '10px',
                    fontWeight: 500,
                    justifyContent: 'flex-start'
                  }}
                >
                  View All Instances
                </Button>
              </Grid>

              <Grid item xs={6}>
                <Button
                  component={RouterLink}
                  to="/oci/costs"
                  fullWidth
                  variant="outlined"
                  startIcon={<CostIcon />}
                  sx={{
                    py: 1.5,
                    textTransform: 'none',
                    borderRadius: '10px',
                    fontWeight: 500,
                    justifyContent: 'flex-start'
                  }}
                >
                  Cost Analysis
                </Button>
              </Grid>

              <Grid item xs={6}>
                <Button
                  component={RouterLink}
                  to="/oci/storage"
                  fullWidth
                  variant="outlined"
                  startIcon={<StorageIcon />}
                  sx={{
                    py: 1.5,
                    textTransform: 'none',
                    borderRadius: '10px',
                    fontWeight: 500,
                    justifyContent: 'flex-start'
                  }}
                >
                  Storage Details
                </Button>
              </Grid>

              <Grid item xs={6}>
                <Button
                  component={RouterLink}
                  to="/oci/compartments"
                  fullWidth
                  variant="outlined"
                  startIcon={<CompartmentIcon />}
                  sx={{
                    py: 1.5,
                    textTransform: 'none',
                    borderRadius: '10px',
                    fontWeight: 500,
                    justifyContent: 'flex-start'
                  }}
                >
                  Compartments
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: '16px',
              boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`
            }}
          >
            <Typography variant="h6" fontWeight="medium" mb={2}>
              Summary Statistics
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Total Boot Volumes
                  </Typography>
                  <Typography variant="h5" fontWeight="medium" sx={{ mt: 1 }}>
                    {formatNumber(summary?.boot_volume_count || 0)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    backgroundColor: alpha(theme.palette.secondary.main, 0.05)
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Total Block Volumes
                  </Typography>
                  <Typography variant="h5" fontWeight="medium" sx={{ mt: 1 }}>
                    {formatNumber(summary?.block_volume_count || 0)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    backgroundColor: alpha(theme.palette.tertiary?.main || '#ffb74d', 0.05)
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Total Storage (GB)
                  </Typography>
                  <Typography variant="h5" fontWeight="medium" sx={{ mt: 1 }}>
                    {formatNumber(summary?.total_storage || 0, 1)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    backgroundColor: alpha(theme.palette.quaternary?.main || '#ff9966', 0.05)
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Active Regions
                  </Typography>
                  <Typography variant="h5" fontWeight="medium" sx={{ mt: 1 }}>
                    {formatNumber(summary?.active_regions || 0)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Memory Details Modal */}
      <Modal
        open={memoryModalOpen}
        onClose={() => setMemoryModalOpen(false)}
        aria-labelledby="memory-details-modal"
        aria-describedby="memory-details-by-instance"
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
          },
        }}
      >
        <Box sx={{
          ...modalStyle,
          backgroundColor: theme.palette.background.paper,
          boxShadow: `0px 8px 24px rgba(0, 0, 0, 0.15)`,
        }}>
          <Typography id="memory-details-modal" variant="h5" component="h2" mb={3} fontWeight="medium">
            Memory Allocation Analysis
          </Typography>

          <Box mb={3} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1">
              Showing instances from {formatDateForAPI(startDate)} to {formatDateForAPI(endDate)}
              {region ? ` in region ${region}` : ''}
              {compartment ? ` for compartment ${compartment}` : ''}
            </Typography>
            <Typography variant="h6" color="tertiary.main" fontWeight="bold">
              Total: {formatNumber(memoryDetails?.analytics?.totalMemory || 0, 1)} GB
            </Typography>
          </Box>

          {/* Memory Analytics Dashboard */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={6} lg={3}>
              <Paper sx={{ p: 2, borderRadius: 2, height: '100%', backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Running Memory</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  {formatNumber(memoryDetails?.analytics?.totalRunningMemory || 0, 1)} GB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatNumber(memoryDetails?.analytics?.totalRunningMemory / (memoryDetails?.analytics?.totalMemory || 1) * 100, 1)}% of total
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Paper sx={{ p: 2, borderRadius: 2, height: '100%', backgroundColor: alpha(theme.palette.warning.main, 0.05) }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Stopped Memory</Typography>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {formatNumber(memoryDetails?.analytics?.totalStoppedMemory || 0, 1)} GB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Could be reclaimed or resized
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Paper sx={{ p: 2, borderRadius: 2, height: '100%', backgroundColor: alpha(theme.palette.tertiary?.main || '#ffb74d', 0.05) }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Avg Per Instance</Typography>
                <Typography variant="h5" fontWeight="bold" color="tertiary.main">
                  {formatNumber(memoryDetails?.analytics?.avgMemoryPerInstance || 0, 1)} GB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatNumber(memoryDetails?.analytics?.avgMemCpuRatio || 0, 1)} GB per CPU
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Paper sx={{ p: 2, borderRadius: 2, height: '100%', backgroundColor: alpha(theme.palette.quaternary?.main || '#ff9966', 0.05) }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Running/Stopped Ratio</Typography>
                <Typography variant="h5" fontWeight="bold" color="quaternary.main">
                  {formatNumber(
                    memoryDetails?.analytics?.totalStoppedMemory ?
                      memoryDetails?.analytics?.totalRunningMemory / memoryDetails?.analytics?.totalStoppedMemory :
                      0,
                    1
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Running:Stopped memory
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Memory by VM Shape */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium' }}>Memory Distribution by Shape</Typography>
          <Grid container spacing={2} mb={4}>
            {memoryDetails?.analytics?.memoryByShape && Object.entries(memoryDetails.analytics.memoryByShape)
              .sort(([, a], [, b]) => b - a)
              .map(([shape, memory], index) => (
                <Grid item xs={6} md={4} lg={3} key={shape}>
                  <Paper sx={{
                    p: 2,
                    borderRadius: 2,
                    height: '100%',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <Typography variant="subtitle2" fontWeight="medium" noWrap title={shape}>
                      {shape}
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      mt: 1,
                      mb: 0.5
                    }}>
                      <Typography variant="h6" fontWeight="bold" color={getChartColors(index).replace(/, [^,]*\)/, ', 1)')}>
                        {formatNumber(memory, 1)} GB
                      </Typography>
                      <Typography variant="body2" color="text.secondary" ml={1}>
                        ({formatNumber(memory / (memoryDetails?.analytics?.totalMemory || 1) * 100, 1)}%)
                      </Typography>
                    </Box>
                    <Box sx={{
                      width: '100%',
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: getChartColors(index),
                      mt: 'auto'
                    }} />
                  </Paper>
                </Grid>
              ))}
          </Grid>

          {/* Remove Instance Memory Details section and add button to view instances */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              component={RouterLink}
              to="/oci/instances"
              variant="outlined"
              startIcon={<InstanceIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              View Instance Details
            </Button>
            <Button
              variant="contained"
              onClick={() => setMemoryModalOpen(false)}
              sx={{ borderRadius: 2 }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* CPU Details Modal */}
      <Modal
        open={cpuModalOpen}
        onClose={() => setCpuModalOpen(false)}
        aria-labelledby="cpu-details-modal"
        aria-describedby="cpu-details-by-instance"
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
          },
        }}
      >
        <Box sx={{
          ...modalStyle,
          backgroundColor: theme.palette.background.paper,
          boxShadow: `0px 8px 24px rgba(0, 0, 0, 0.15)`,
        }}>
          <Typography id="cpu-details-modal" variant="h5" component="h2" mb={3} fontWeight="medium">
            CPU Allocation Analysis
          </Typography>

          <Box mb={3} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1">
              Showing instances from {formatDateForAPI(startDate)} to {formatDateForAPI(endDate)}
              {region ? ` in region ${region}` : ''}
              {compartment ? ` for compartment ${compartment}` : ''}
            </Typography>
            <Typography variant="h6" color="secondary.main" fontWeight="bold">
              Total: {formatNumber(cpuDetails?.analytics?.totalCpu || 0)} Cores
            </Typography>
          </Box>

          {/* CPU Analytics Dashboard */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={6} lg={3}>
              <Paper sx={{ p: 2, borderRadius: 2, height: '100%', backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Running CPU Cores</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  {formatNumber(cpuDetails?.analytics?.totalRunningCpu || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatNumber(cpuDetails?.analytics?.totalRunningCpu / (cpuDetails?.analytics?.totalCpu || 1) * 100, 1)}% of total
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Paper sx={{ p: 2, borderRadius: 2, height: '100%', backgroundColor: alpha(theme.palette.warning.main, 0.05) }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Stopped CPU Cores</Typography>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {formatNumber(cpuDetails?.analytics?.totalStoppedCpu || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Could be reclaimed or reassigned
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Paper sx={{ p: 2, borderRadius: 2, height: '100%', backgroundColor: alpha(theme.palette.secondary.main, 0.05) }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Avg Per Instance</Typography>
                <Typography variant="h5" fontWeight="bold" color="secondary.main">
                  {formatNumber(cpuDetails?.analytics?.avgCpuPerInstance || 0, 1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cores per instance
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <Paper sx={{ p: 2, borderRadius: 2, height: '100%', backgroundColor: alpha(theme.palette.quaternary?.main || '#ff9966', 0.05) }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Memory Per CPU</Typography>
                <Typography variant="h5" fontWeight="bold" color="quaternary.main">
                  {formatNumber(cpuDetails?.analytics?.avgCpuMemRatio || 0, 1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  GB per CPU core
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* CPU by VM Shape */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium' }}>CPU Distribution by Shape</Typography>
          <Grid container spacing={2} mb={4}>
            {cpuDetails?.analytics?.cpuByShape && Object.entries(cpuDetails.analytics.cpuByShape)
              .sort(([, a], [, b]) => b - a)
              .map(([shape, cpuCount], index) => (
                <Grid item xs={6} md={4} lg={3} key={shape}>
                  <Paper sx={{
                    p: 2,
                    borderRadius: 2,
                    height: '100%',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <Typography variant="subtitle2" fontWeight="medium" noWrap title={shape}>
                      {shape}
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      mt: 1,
                      mb: 0.5
                    }}>
                      <Typography variant="h6" fontWeight="bold" color={getChartColors(index).replace(/, [^,]*\)/, ', 1)')}>
                        {formatNumber(cpuCount)} Cores
                      </Typography>
                      <Typography variant="body2" color="text.secondary" ml={1}>
                        ({formatNumber(cpuCount / (cpuDetails?.analytics?.totalCpu || 1) * 100, 1)}%)
                      </Typography>
                    </Box>
                    <Box sx={{
                      width: '100%',
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: getChartColors(index),
                      mt: 'auto'
                    }} />
                  </Paper>
                </Grid>
              ))}
          </Grid>

          {/* Button to view instances */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              component={RouterLink}
              to="/oci/instances"
              variant="outlined"
              startIcon={<InstanceIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              View Instance Details
            </Button>
            <Button
              variant="contained"
              onClick={() => setCpuModalOpen(false)}
              sx={{ borderRadius: 2 }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Cost Details Modal */}
      <Modal
        open={costModalOpen}
        onClose={() => setCostModalOpen(false)}
        aria-labelledby="cost-details-modal"
        aria-describedby="cost-details-breakdown"
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
          },
        }}
      >
        <Box sx={{
          ...modalStyle,
          backgroundColor: theme.palette.background.paper,
          boxShadow: `0px 8px 24px rgba(0, 0, 0, 0.15)`,
        }}>
          <Typography id="cost-details-modal" variant="h5" component="h2" mb={3} fontWeight="medium">
            Cost Analysis
          </Typography>

          <Box mb={3} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1">
              Showing costs from {formatDateForAPI(startDate)} to {formatDateForAPI(endDate)}
              {region ? ` in region ${region}` : ''}
              {compartment ? ` for compartment ${compartment}` : ''}
            </Typography>
            <Typography variant="h6" color="quaternary.main" fontWeight="bold">
              Total Cost: {formatNumber(costDetails?.analytics?.grandTotal || 0, 2, true)}
            </Typography>
          </Box>

          {/* Cost Analytics Dashboard */}
          <Grid container spacing={2} mb={4}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%', backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Typography variant="subtitle1" fontWeight="medium" color="text.primary" gutterBottom>
                  Cost Overview
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">Total Period Cost</Typography>
                        <Typography variant="body1" fontWeight="medium" color="text.primary">
                          {formatNumber(costDetails?.analytics?.grandTotal || 0, 2, true)}
                        </Typography>
                      </Box>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">Daily Average Cost</Typography>
                        <Typography variant="body1" fontWeight="medium" color="text.primary">
                          {formatNumber(costDetails?.analytics?.avgDailyTotal || 0, 2, true)}
                        </Typography>
                      </Box>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">Number of Days</Typography>
                        <Typography variant="body1" fontWeight="medium" color="text.primary">
                          {costDetails?.analytics?.days || 0}
                        </Typography>
                      </Box>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">Date Range</Typography>
                        <Typography variant="body1" fontWeight="medium" color="text.primary">
                          {formatDateForAPI(startDate)} - {formatDateForAPI(endDate)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%', backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Typography variant="subtitle1" fontWeight="medium" color="text.primary" gutterBottom>
                  Cost Distribution
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {[
                    { label: 'Compute Services', value: costDetails?.analytics?.totalCompute || 0, color: theme.palette.primary.main },
                    { label: 'Storage Services', value: costDetails?.analytics?.totalStorage || 0, color: theme.palette.secondary.main },
                    { label: 'Network Services', value: costDetails?.analytics?.totalNetwork || 0, color: theme.palette.tertiary?.main || '#ffb74d' },
                    { label: 'Database Services', value: costDetails?.analytics?.totalDatabase || 0, color: theme.palette.quaternary?.main || '#ff9966' },
                    { label: 'Other Services', value: costDetails?.analytics?.totalOther || 0, color: '#9575cd' }
                  ].sort((a, b) => b.value - a.value).map((item, index) => {
                    const percentage = (item.value / (costDetails?.analytics?.grandTotal || 1)) * 100;
                    return (
                      <Box key={item.label} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" fontWeight="medium">{item.label}</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatNumber(item.value, 2, true)} ({formatNumber(percentage, 1)}%)
                          </Typography>
                        </Box>
                        <Box sx={{ position: 'relative', height: 24, bgcolor: alpha(item.color, 0.1), borderRadius: 2 }}>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              height: '100%',
                              width: `${percentage}%`,
                              bgcolor: item.color,
                              borderRadius: 2
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Daily Cost Breakdown Table */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium' }}>Daily Cost Breakdown</Typography>
          <Paper
            sx={{
              p: 0,
              mb: 4,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              overflow: 'hidden'
            }}
          >
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'medium', backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'medium', backgroundColor: alpha(theme.palette.primary.main, 0.05) }} align="right">Compute</TableCell>
                    <TableCell sx={{ fontWeight: 'medium', backgroundColor: alpha(theme.palette.primary.main, 0.05) }} align="right">Storage</TableCell>
                    <TableCell sx={{ fontWeight: 'medium', backgroundColor: alpha(theme.palette.primary.main, 0.05) }} align="right">Network</TableCell>
                    <TableCell sx={{ fontWeight: 'medium', backgroundColor: alpha(theme.palette.primary.main, 0.05) }} align="right">Database</TableCell>
                    <TableCell sx={{ fontWeight: 'medium', backgroundColor: alpha(theme.palette.primary.main, 0.05) }} align="right">Other</TableCell>
                    <TableCell sx={{ fontWeight: 'medium', backgroundColor: alpha(theme.palette.primary.main, 0.05) }} align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {costDetails?.rawData?.sort((a, b) => new Date(b.date) - new Date(a.date)).map((day, index) => {
                    // Calculate storage, network and other costs
                    const storageTotal = (day.total_block_storage_cost || 0) +
                      (day.total_file_storage_cost || 0) +
                      (day.total_object_storage_cost || 0);

                    const networkTotal = (day.total_load_balancer_cost || 0) +
                      (day.total_virtual_cloud_network_cost || 0);

                    const otherTotal = (day.total_functions_cost || 0) +
                      (day.total_telemetry_cost || 0) +
                      (day.total_api_gateway_cost || 0) +
                      (day.total_container_engine_cost || 0) +
                      (day.total_key_management_cost || 0) +
                      (day.total_logging_cost || 0);

                    return (
                      <TableRow
                        key={day.date}
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: alpha(theme.palette.primary.main, 0.02) }
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {day.date}
                        </TableCell>
                        <TableCell align="right">{formatNumber(day.total_compute_cost || 0, 2, true)}</TableCell>
                        <TableCell align="right">{formatNumber(storageTotal, 2, true)}</TableCell>
                        <TableCell align="right">{formatNumber(networkTotal, 2, true)}</TableCell>
                        <TableCell align="right">{formatNumber(day.total_mysql_cost || 0, 2, true)}</TableCell>
                        <TableCell align="right">{formatNumber(otherTotal, 2, true)}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 'medium', color: theme.palette.quaternary?.main || theme.palette.primary.main }}
                        >
                          {formatNumber(day.grand_total_cost || 0, 2, true)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Button to view detailed costs */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              component={RouterLink}
              to="/oci/costs"
              variant="outlined"
              startIcon={<CostIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              View Detailed Cost Analysis
            </Button>
            <Button
              variant="contained"
              onClick={() => setCostModalOpen(false)}
              sx={{ borderRadius: 2 }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default OCIDashboard;